<template>
  <div
    v-if="!isLoading2"
    class="section"
  >
    <div class="columns is-centered">
      <div class="column is-11">
        <div class="columns">
          <div class="column has-text-centered">
            <h2 class="title is-2">
              Review Statements
            </h2>
            <div class="subtitle is-2">
              Prior to submission, review and make any necessary corrections to the data you entered. Use the table below to review and correct the information you entered within each section.
            </div>
          </div>
        </div>
        <div class="columns">
          <div class="column">
            <div v-if="incompletedScreens.length !== 0">
              <div class="message is-warning">
                <div class="message-body">
                  You have not entered all required information. Please complete the necessary sections below. Make sure to always click the <strong>"Save & Continue"</strong> button on each form page to save your changes. When you are satisfied that all information is correct, and are back to this screen, click the "Submit" button below to proceed with submission.
                </div>
              </div>
            </div>
            <div v-else>
              <div class="message is-info">
                <div class="message-body">
                  You have sucessfully completed every section. Review your information, as needed, and proceed below.
                </div>
              </div>
            </div>
            <br>
            <table class="table is-bordered is-striped is-fullwidth table-content-center">
              <thead>
                <tr>
                  <th>Section Name</th>
                  <th>Status</th>
                  <th class="has-text-centered">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="screen in requiredScreens"
                  :key="screen"
                >
                  <td>{{ screenNamesMap[screen] }}</td>
                  <td class="completion-status">
                    <template v-if="incompletedScreens.includes(screen)">
                      Not completed
                      <span class="has-text-danger">
                        <i
                          class="fa fa-exclamation-circle"
                          aria-hidden="true"
                        />
                      </span>
                    </template>
                    <template v-else>
                      Completed
                    </template>
                  </td>
                  <td class="has-text-centered">
                    <template v-if="incompletedScreens.includes(screen)">
                      <router-link :to="screen">
                        <button
                          id="update-btn"
                          type="button"
                          class="button is-warning"
                        >
                          update
                        </button>
                      </router-link>
                    </template>
                    <template v-else>
                      <router-link :to="screen">
                        <button
                          id="review-btn"
                          type="button"
                          class="button is-secondary"
                        >
                          Review
                        </button>
                      </router-link>
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="incompletedScreens.length === 0">
              <div class="section">
                <div class="columns">
                  <div class="column">
                    <h2 class="title is-2">
                      Preview Statements
                    </h2>
                    <div class="subtitle is-2">
                      Use the links below to preview each form before you submit them.
                    </div>
                  </div>
                </div>
                <div class="preview-forms">
                  <div class="columns">
                    <template v-if="currentFiling.cityForm === 'Y'">
                      <div class="column is-2">
                        <a
                          href="javascript:;"
                          class="preview-link"
                          :disabled="loading['City']"
                          @click.prevent="generatePdfPreview('City')"
                        >
                          <i class="far fa-file-pdf" />
                          City Form<br> Preview
                          <div v-if="loading['City']">generating pdf...</div>
                          <progress
                            v-if="loading['City']"
                            class="progress is-danger is-small"
                            max="100"
                          />
                        </a>
                      </div>
                    </template>
                    <template v-if="currentFiling.stateForm === 'Y'">
                      <div class="column is-2">
                        <a
                          href="javascript:;"
                          class="preview-link"
                          :disabled="loading['State']"
                          @click.prevent="generatePdfPreview('State')"
                        >
                          <i class="far fa-file-pdf" />
                          State Form<br> Preview
                          <div v-if="loading['State']">generating pdf...</div>
                          <progress
                            v-if="loading['State']"
                            class="progress is-danger is-small"
                            max="100"
                          />
                        </a>
                      </div>
                    </template>
                    <template v-if="currentFiling.mayorForm === 'Y' && showMayorForm">
                      <div class="column is-2">
                        <a
                          href="javascript:;"
                          class="preview-link"
                          :disabled="loading['Mayor']"
                          @click.prevent="generatePdfPreview('Mayor')"
                        >
                          <i class="far fa-file-pdf" />
                          Mayor Form<br> Preview
                          <div v-if="loading['Mayor']">generating pdf...</div>
                          <progress
                            v-if="loading['Mayor']"
                            class="progress is-danger is-small"
                            max="100"
                          />
                        </a>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="columns">
          <div class="column has-text-centered">
            <template v-if="incompletedScreens.length === 0">
              <router-link :to="nextFilingScreen">
                <button
                  id="continue-btn"
                  type="button"
                  class="button is-primary"
                  :class="isSaving ? 'is-loading' : ''"
                  :disabled="incompletedScreens.length !== 0"
                >
                  Continue to Statements Submission
                </button>
              </router-link>
            </template>
            <template v-else>
              <button
                id="submit-btn"
                type="button"
                class="button is-primary"
                :class="isSaving ? 'is-loading' : ''"
                disabled
              >
                Submit Statements
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { filerScreenHelpers } from '@/mixins/filerScreen';
import { pdfData } from '@/mixins/pdfData';
export default {
  name: "ReviewStatements",
  mixins: [
    filerScreenHelpers,
    pdfData,
  ],
  data() {
    return {
      isSaving: false,
      currentFiling: {},
      incompletedScreens: [],
      requiredScreens: [],
      screenNamesMap: {
        'filer-information': 'Filer Information',
        'income-sources': 'Income Sources',
        'creditors': 'Creditors',
        'real-estate': 'Real Estate',
        'gifts': 'Gifts',
        'directorships': 'Directorships',
        'business-interest': 'Business Interests',
        'business-interest-transferred': 'Business Interests Transfer',
        'family': 'Family Members',
      },
      loading: {
        City: false,
        State: false,
        Mayor: false,
      },
      pdfUrl: {
        City: '#',
        State: '#',
        Mayor: '#',
      },
    };
  },
  async created () {
    await this.initialFetch();
    await this.initialAssign();
    this.$store.commit('loading', false);
  },
  methods: {
    getIncompletedScreems () {
      let filing = this.currentFiling;

      let screenStatusMap = {
        'filer-information': 'filerInformationCompleted',
        'income-sources': 'incomeCompleted',
        'creditors': 'creditorsCompleted',
        'real-estate': 'realEstateCompleted',
        'gifts': 'giftsCompleted',
        'directorships': 'directorshipsCompleted',
        'business-interest': 'businessInterestCompleted',
        'business-interest-transferred': 'businessInttTransCompleted',
        'family': 'familymembersCompleted',
      };

      let screensNotCompleted = [];

      this.requiredScreens.forEach(screen => {
        if (typeof screenStatusMap[screen] !== 'undefined' && typeof filing[screenStatusMap[screen]] !== 'undefined') {
          if (filing[screenStatusMap[screen]] !== 'Y') {
            this.$router.options.routes.find(route => {
              return route.name === screen;
            });
            screensNotCompleted.push(screen);
          }
        }
      });

      return screensNotCompleted;
    },
    getRequirementScreens () {
      let requiredScreens = this.cloneObj(this.$store.getters['filings/filingScreens']('filing'));

      let screensToRemove = [
        'file-now',
        'user-profile-update',
        'review-statements',
        'submit-statements',
        'statements-submitted',
        'edit-user-profile',
      ];

      let screensToUse = [];

      requiredScreens.forEach(screen => {
        if (!screensToRemove.includes(screen)) {
          screensToUse.push(screen);
        }
      });

      return screensToUse;
    },
    async initialAssign () {
      this.currentFiling = await this.getCurrentFiling();
      this.requiredScreens = await this.getRequirementScreens();
      this.incompletedScreens = await this.getIncompletedScreems();
    },
    async initialFetch () {
      let promises = [];
      return Promise.all(promises);
    },
    async generatePdfPreview(type) {

      this.loading[type] = true;

      let formData = await this.pdf_getPdfData(type, this.currentFiling.id, true);
      let pdfUrl = await this.$store.dispatch('filing/getPdf', formData);

      if (pdfUrl) {
        window.open(pdfUrl);
      } else {
        this.$warning("There was an issue retrieving the pdf preview");
      }

      this.loading[type] = false;

    },
  },
};
</script>
<style lang="scss" scoped>
  table tr td {
    vertical-align: middle;
  }
  .preview-link {
    border: 1px solid #ccc;
    width: 100%;
    text-align: center;
    margin: 0 auto;
    padding: 30px 0;
    display: inline-block;
    position: relative;
    &:hover{
      background-color: #fff;
    }
    .progress {
      position: absolute;
      bottom: 0;
      left: 0;
      height: 3px;
    }
  }
  .preview-forms {
    .fa-file-pdf {
      display: block;
      margin-bottom: 10px;
      &:before {
        font-size: 30px;
        color: red;
      }
    }
  }
</style>
